import { Plane, Vector3 } from "three";
import ExtrudedLoopAnnotationSceneObject from "./ExtrudedLoopAnnotationSceneObject";
import RectangleAnnotationData from "../sceneObjectData/RectangleAnnotationData";
import { isPlaneEdgeOn } from "../../utils/editHandleVisibility";
import EditHandleData from "../sceneObjectData/EditHandleData";
import EditHandleTypeEnum from "../../enums/EditHandleTypeEnum";
import CommandIdEnum from "../../enums/CommandIdEnums";
import PropertyList from "../properties/PropertyList";
import { PropertyId } from "../properties/PropertyId";
import PropertyItem from "../properties/PropertyItem";
import { PID_RECT_WIDTH, PID_RECT_HEIGHT } from "../properties/PropertyIds";
import { CATEGORY_DIMENSIONS } from "../properties/PropertyCategoryRegistry";
import lengthCmTranslator from "../properties/translators/lengthCmTranslator";

export default class RectangleAnnotationSceneObject extends ExtrudedLoopAnnotationSceneObject<RectangleAnnotationData> {

    getProperties(props: PropertyList, filterId?: PropertyId): void {
        super.getProperties(props, filterId);

        const { c1, c2 } = this.data.getLocalCorners();

        const width = props.getOrCreateProperty(PID_RECT_WIDTH, () => new PropertyItem(PID_RECT_WIDTH, lengthCmTranslator));
        width.value = Math.abs(c2.x - c1.x);
        width.categoryId = CATEGORY_DIMENSIONS.id;

        const height = props.getOrCreateProperty(PID_RECT_HEIGHT, () => new PropertyItem(PID_RECT_HEIGHT, lengthCmTranslator));
        height.value = Math.abs(c2.y - c1.y);
        height.categoryId = CATEGORY_DIMENSIONS.id;
    }

    // super.setProperties() already applied fontSize (and its own geometry update, see
    // ExtrudedLoopAnnotationSceneObject) — width/height are applied here on top by repositioning
    // corner2 only, so corner1 stays the fixed anchor and the rectangle's existing orientation
    // (which side corner2 extends towards) is preserved.
    setProperties(props: PropertyList): void {
        super.setProperties(props);

        const { localX, localY } = this.data.getLocalBasis();
        const { c1, c2 } = this.data.getLocalCorners();
        let c2x = c2.x;
        let c2y = c2.y;

        const width = props.getProperty<number>(PID_RECT_WIDTH);
        if (width) c2x = c1.x + (Math.sign(c2.x - c1.x) || 1) * width.value;

        const height = props.getProperty<number>(PID_RECT_HEIGHT);
        if (height) c2y = c1.y + (Math.sign(c2.y - c1.y) || 1) * height.value;

        this.data.corner2 = localX.clone().multiplyScalar(c2x).add(localY.clone().multiplyScalar(c2y));

        this.geometry.update(this.data, false);
    }

    getEditHandles(localViewDirection: Vector3): EditHandleData[] {
        const data = this.data;

        // Seen edge-on the rectangle is just a line, so dragging its corners in-plane is pointless.
        if (isPlaneEdgeOn(localViewDirection, data.placement.axis))
            return super.getEditHandles(localViewDirection);

        // placement.axis is the profile plane's normal (Z); refDirection is the in-plane X axis.
        const plane = new Plane().setFromNormalAndCoplanarPoint(
            data.placement.axis.clone(),
            data.placement.location.clone()
        );

        return [
            ...super.getEditHandles(localViewDirection),
            new EditHandleData(data.uuid, EditHandleTypeEnum.RECTANGLE_CORNER,
                data.placement.location.clone().add(data.corner1), CommandIdEnum.GeometryMoveEdit,
                (d, delta) => { (d as RectangleAnnotationData).corner1.add(delta); },
                plane.clone()),
            new EditHandleData(data.uuid, EditHandleTypeEnum.RECTANGLE_CORNER,
                data.placement.location.clone().add(data.corner2), CommandIdEnum.GeometryMoveEdit,
                (d, delta) => { (d as RectangleAnnotationData).corner2.add(delta); },
                plane.clone()),
        ];
    }
}
